import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, Checkbox, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueFragment, ShippableContainerItemFragment, UnitType } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useTenant } from "hooks/useTenant";
import { DateTime } from "luxon";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toUtcMidnightIsoFromDateLike } from "util/dates";

import { WasteFacilityScopeBar } from "../components/WasteFacilityScopeBar";
import { formatQuantity } from "../Containers/containerDisplay";
import {
  CREATE_WASTE_SHIPMENT_MUTATION,
  SHIPMENT_REFETCH_QUERIES,
  SHIPPABLE_CONTAINERS_QUERY,
  VALIDATE_WASTE_SHIPMENT_INPUT,
} from "./api";
import { daysUntil } from "./shipmentDisplay";
import {
  groupContainersByStream,
  ShippableContainerStreamGroup,
} from "./shippableContainerGrouping";
import {
  ShippableContainerGroup,
  ShippableContainerHeader,
  ShippableContainerListSkeleton,
} from "./ShippableContainerRow";
import { useContainerSelection } from "./useContainerSelection";

const SOON_DUE_DAYS = 30;

/**
 * The picker is a hand-built row layout rather than a table, so its header and
 * its rows have to agree on column widths independently. Keeping the widths
 * here means widening a column can't silently misalign the label above it.
 *
 * The header's leading spacer stands in for each row's `size="small"` Checkbox:
 * a 20px icon inside 9px of padding on each side.
 */

/**
 * What this facility has ready to ship, plus the two things the page says
 * about the set as a whole.
 */
function useShippableContainers(facilityId: string | null) {
  const { data, loading } = useQuery(SHIPPABLE_CONTAINERS_QUERY, {
    variables: { facilityId: facilityId ?? "" },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });

  const containers = useMemo(
    () => data?.facility?.shippableContainers?.items ?? [],
    [data?.facility?.shippableContainers?.items]
  );
  const groups = useMemo(() => groupContainersByStream(containers), [containers]);
  const soonDueCount = useMemo(
    () =>
      containers.filter((c) => {
        const days = daysUntil(c.clock.effectiveDeadlineDate);
        return days !== null && days <= SOON_DUE_DAYS;
      }).length,
    [containers]
  );

  return {
    containers,
    groups,
    soonDueCount,
    // While switching facilities the query has no cached result for the new
    // facility yet, so `data` is briefly undefined. Treat that as loading (not
    // "0 containers") so the count/empty-state don't flash a stale zero.
    loadingContainers: loading && data?.facility == null,
  };
}

/**
 * Warn-level validation (e.g. a picked container already on another open
 * shipment) as the selection changes; CRITICAL rules are enforced by the
 * mutation itself.
 */
function useShipmentValidationIssues(
  facilityId: string | null,
  selectedIds: ReadonlySet<string>
): IssueFragment[] {
  const [validate, { data, previousData }] = useLazyQuery(VALIDATE_WASTE_SHIPMENT_INPUT, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (facilityId && selectedIds.size > 0) {
      validate({ variables: { input: { facilityId, containerIds: [...selectedIds] } } });
    }
  }, [facilityId, selectedIds, validate]);

  // Fall back to the prior result while a re-validation is in flight so the
  // critical-issue alert stays mounted and only its text updates — otherwise
  // the no-cache query blanks data mid-flight and the alert flashes.
  const effective = data ?? previousData;
  const issues: IssueFragment[] =
    selectedIds.size > 0 ? (effective?.wasteShipmentInputValidator ?? []) : [];
  return issues.filter((issue) => issue.level === "CRITICAL");
}

/**
 * The live "manifest request" summary: the selected containers rolled up the
 * way the backend will group them, so the forming request is legible before
 * it is created.
 */
function useRequestPreview(
  containers: ShippableContainerItemFragment[],
  selectedIds: ReadonlySet<string>
) {
  const selected = useMemo(
    () => containers.filter((c) => selectedIds.has(c.id)),
    [containers, selectedIds]
  );

  // Distinct streams on the request — a stream split across units still
  // counts once, so the header never disagrees with what the user selected.
  const selectedStreamCount = useMemo(
    () => new Set(selected.map((c) => c.wasteStream?.id ?? "")).size,
    [selected]
  );

  // Split per stream AND unit so the figures stay dimensionally honest.
  const requestLines = useMemo(() => {
    const lines = new Map<
      string,
      { streamName: string | null; count: number; total: number; unit: UnitType | null }
    >();
    for (const container of selected) {
      const unit = container.amountUnit ?? container.capacityUnit;
      const key = `${container.wasteStream?.id ?? ""}|${unit ?? ""}`;
      const line = lines.get(key) ?? {
        streamName: container.wasteStream?.name ?? null,
        count: 0,
        total: 0,
        unit: unit ?? null,
      };
      line.count += 1;
      line.total += container.amount ?? 0;
      lines.set(key, line);
    }
    return [...lines.values()].sort((a, b) =>
      (a.streamName ?? "\uffff").localeCompare(b.streamName ?? "\uffff")
    );
  }, [selected]);

  const previewStreamSummary = useMemo(() => {
    // Collapsed back to one entry per stream for the summary line: naming the
    // stream on each unit read as two streams — a single stream held in gallons
    // and pounds printed its own name twice in a row.
    const byStream = new Map<string, string[]>();
    for (const line of requestLines) {
      const name = line.streamName ?? "Uncharacterized";
      const qty = line.unit ? formatQuantity(line.total, line.unit) : `${line.count} cont`;
      byStream.set(name, [...(byStream.get(name) ?? []), qty]);
    }
    const entries = [...byStream.entries()];
    const shown = entries.slice(0, 2).map(([name, qtys]) => `${name} ${qtys.join(" \u00b7 ")}`);
    const more = entries.length - shown.length;
    return shown.join(" \u00b7 ") + (more > 0 ? ` \u00b7 +${more} more` : "");
  }, [requestLines]);

  return { selectedStreamCount, previewStreamSummary };
}

/**
 * Creating the shipment and handing off to it.
 *
 * The refetch list is the shared one, not a hand-picked pair: creating a
 * shipment claims its containers, so the container reads go stale too — and
 * the drawer's Request-manifest gating reads `openShipments`, which is exactly
 * what a create invalidates.
 */
function useCreateShipment({
  facilityId,
  selectedIds,
  plannedShipDate,
  tenantId,
}: {
  facilityId: string | null;
  selectedIds: ReadonlySet<string>;
  plannedShipDate: DateTime | null;
  tenantId: string | undefined;
}) {
  const alerts = useAlerts();
  const navigate = useNavigate();
  const [createShipment, { loading: creating }] = useMutation(CREATE_WASTE_SHIPMENT_MUTATION, {
    refetchQueries: SHIPMENT_REFETCH_QUERIES,
  });

  const handleCreate = async () => {
    if (!facilityId || selectedIds.size === 0) {
      return;
    }
    try {
      const result = await createShipment({
        variables: {
          input: {
            facilityId,
            containerIds: [...selectedIds],
            // Serialized the way the detail page serializes the same field, so
            // a date set here and a date set there mean the same calendar day.
            ...(plannedShipDate
              ? { plannedShipDate: toUtcMidnightIsoFromDateLike(plannedShipDate) }
              : {}),
          },
        },
      });
      const created = result.data?.createWasteShipment;
      alerts.success(`Shipment ${created?.manifestRequestNumber ?? ""} created.`);
      if (created) {
        // Header details (vendor, transporter, TSDF, dates) are filled in on
        // the shipment itself — land there.
        navigate(`/o/${tenantId}/waste/shipments/${created.id}`);
      }
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to create the shipment.");
    }
  };

  return { handleCreate, creating };
}

/**
 * The ready-to-ship list: a master checkbox over the containers this facility
 * has in central accumulation, grouped by waste stream.
 */
function ShippableContainerPanel({
  containers,
  groups,
  loading,
  selectedIds,
  onToggle,
  onSetSelection,
}: {
  containers: ShippableContainerItemFragment[];
  groups: ShippableContainerStreamGroup[];
  loading: boolean;
  selectedIds: Set<string>;
  onToggle: (containerId: string) => void;
  onSetSelection: (containerIds: string[], selected: boolean) => void;
}) {
  // Master checkbox: checked when every ready container is selected,
  // indeterminate when only some are.
  const allSelected = containers.length > 0 && containers.every((c) => selectedIds.has(c.id));
  const someSelected = containers.some((c) => selectedIds.has(c.id));

  return (
    <Paper elevation={0} sx={{ py: 2 }}>
      {/* px matches the group headers and rows so the master checkbox lines up
          in the same column as the ones below it. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 1 }}>
        <Checkbox
          size="small"
          checked={allSelected}
          indeterminate={someSelected && !allSelected}
          disabled={loading || containers.length === 0}
          onChange={(e) =>
            onSetSelection(
              containers.map((c) => c.id),
              e.target.checked
            )
          }
          inputProps={{ "aria-label": "Select all ready containers" }}
        />
        {loading ? (
          <Skeleton variant="text" width={260} />
        ) : (
          <Typography variant="subtitle1">
            {containers.length} container{containers.length === 1 ? "" : "s"} ready in central
            accumulation
          </Typography>
        )}
      </Stack>

      {loading && <ShippableContainerListSkeleton />}

      {!loading && containers.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No central-accumulation containers are ready to ship at this facility.
        </Typography>
      )}

      {!loading && containers.length > 0 && <ShippableContainerHeader />}

      {groups.map((group) => (
        <ShippableContainerGroup
          key={group.streamId || "uncharacterized"}
          group={group}
          selectedIds={selectedIds}
          onToggle={onToggle}
          onToggleGroup={onSetSelection}
        />
      ))}
    </Paper>
  );
}

/**
 * Pins the request summary to the bottom of the viewport.
 *
 * The list is as long as the facility has drums, and selecting from it used to
 * mean scrolling past everything you had just chosen to reach the button that
 * acts on it. Opaque with a rule above so the rows pass underneath rather than
 * through the summary describing them.
 */
function StickyFooter({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        px: 3,
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        backgroundColor: "background.paper",
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      {children}
    </Box>
  );
}

/**
 * The page's own heading, the unscoped prompt, and the deadline nudge — all of
 * which describe the list rather than acting on it.
 *
 * No facility overline: the scope bar above already names the facility, and
 * repeating it here said the same thing twice, while unscoped it left a bare
 * "Shipment" label standing over nothing.
 */
function PageIntro({
  facilityId,
  soonDueCount,
}: {
  facilityId: string | null;
  soonDueCount: number;
}) {
  return (
    <>
      <Box sx={{ px: 3 }}>
        <Typography variant="h5">Package a shipment</Typography>
        <Typography variant="body2" color="text.secondary">
          Select what&apos;s leaving central accumulation. Encamp builds a manifest request for your
          vendor — it isn&apos;t the official manifest.
        </Typography>
      </Box>

      {!facilityId && (
        <Box sx={{ px: 3, pt: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Choose a facility above to see what it has ready to ship.
          </Typography>
        </Box>
      )}

      {facilityId && soonDueCount > 0 && (
        <Box sx={{ px: 3 }}>
          <Alert severity="warning">
            <strong>
              {soonDueCount} container{soonDueCount === 1 ? " is" : "s are"} due within{" "}
              {SOON_DUE_DAYS} days.
            </strong>{" "}
            {soonDueCount === 1
              ? "Ship it to stay compliant."
              : "Ship them together to stay compliant and minimize hauls."}
          </Alert>
        </Box>
      )}
    </>
  );
}

export function PackageShipmentBuilder() {
  const { tenantId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");
  // Containers the caller already picked — a container drawer's "Request
  // manifest" lands here with its own container in hand. Read once on mount:
  // the hook seeds from it and the user owns the selection from then on.
  const [initialContainerIds] = useState(() =>
    (searchParams.get("containerIds") ?? "").split(",").filter(Boolean)
  );

  const { selectedIds, toggle, setSelection, clear } = useContainerSelection(initialContainerIds);

  // Captured here rather than left for the shipment page: the date is the one
  // header field the person packaging the load already knows — they are picking
  // these containers because of when the waste has to be gone — and
  // CreateWasteShipmentInput has always accepted it. The rest of the header
  // (vendor, transporter, TSDF) is someone else's answer and still waits.
  const [plannedShipDate, setPlannedShipDate] = useState<DateTime | null>(null);

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments", to: tenantId ? `/o/${tenantId}/waste/shipments` : undefined },
    { label: "Package a shipment" },
  ]);

  const { containers, loadingContainers, groups, soonDueCount } =
    useShippableContainers(facilityId);

  const criticalIssues = useShipmentValidationIssues(facilityId, selectedIds);

  const { selectedStreamCount, previewStreamSummary } = useRequestPreview(containers, selectedIds);

  const handleFacilityChange = (nextFacilityId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (nextFacilityId) {
      next.set("facilityId", nextFacilityId);
    } else {
      next.delete("facilityId");
    }
    setSearchParams(next, { replace: true });
    // A selection is a set of this facility's containers; carrying it across a
    // scope change would build a shipment out of another facility's drums.
    clear();
  };

  const { handleCreate, creating } = useCreateShipment({
    facilityId,
    selectedIds,
    plannedShipDate,
    tenantId,
  });

  if (!tenantId) {
    return null;
  }

  return (
    <>
      {/* The same scope bar the container and shipment lists draw, so arriving
          here from either one keeps the facility control where it already was.
          Packaging cannot span facilities — a shipment belongs to one — so the
          unscoped state asks for a facility rather than offering "All". */}
      <WasteFacilityScopeBar
        facilityId={facilityId}
        onChangeFacility={handleFacilityChange}
        placeholder="Select a facility"
      />
      <Stack spacing={2} sx={{ py: 2 }}>
        <PageIntro facilityId={facilityId} soonDueCount={soonDueCount} />

        {facilityId && (
          <Box sx={{ px: 3 }}>
            <ShippableContainerPanel
              containers={containers}
              groups={groups}
              loading={loadingContainers}
              selectedIds={selectedIds}
              onToggle={toggle}
              onSetSelection={setSelection}
            />
          </Box>
        )}

        {facilityId && containers.length > 0 && (
          <StickyFooter>
            <RequestBar
              containerCount={selectedIds.size}
              streamCount={selectedStreamCount}
              streamSummary={previewStreamSummary}
              criticalIssues={criticalIssues}
              plannedShipDate={plannedShipDate}
              onPlannedShipDateChange={setPlannedShipDate}
              creating={creating}
              onCreate={handleCreate}
            />
          </StickyFooter>
        )}
      </Stack>
    </>
  );
}

/**
 * What the request currently amounts to, and the button that commits it.
 *
 * Its own component because it is the half of the page that does not scroll:
 * everything above is the list being chosen from, and this is the standing
 * answer to "what have I chosen". Splitting it also keeps the builder's own
 * body readable, which it had stopped being.
 */
function RequestBar({
  containerCount,
  streamCount,
  streamSummary,
  criticalIssues,
  plannedShipDate,
  onPlannedShipDateChange,
  creating,
  onCreate,
}: {
  containerCount: number;
  streamCount: number;
  streamSummary: string;
  criticalIssues: IssueFragment[];
  plannedShipDate: DateTime | null;
  onPlannedShipDateChange: (value: DateTime | null) => void;
  creating: boolean;
  onCreate: () => void;
}) {
  return (
    <Paper elevation={0} sx={{ py: 2 }}>
      {/* Open-shipment double-packaging shows per row (warning icon);
          only the rare blocking issues surface here as a safety net. */}
      {criticalIssues.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          {criticalIssues.map((issue, i) => (
            <Alert key={`crit-${issue.key}-${i}`} severity="error">
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={2}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2">
            {containerCount} container{containerCount === 1 ? "" : "s"} · {streamCount} stream
            {streamCount === 1 ? "" : "s"}
            {streamSummary ? ` · ${streamSummary}` : ""}
          </Typography>
          {/* Only what is not already said above. The page's own description
              covers "this is a request, not the manifest"; repeating it here
              put a two-line paragraph in the one bar that has to stay
              scannable while the reader is still picking drums. */}
          <Typography variant="caption" color="text.secondary">
            Vendor and transporter are added on the shipment after it&apos;s created.
          </Typography>
        </Box>
        <DatePicker
          label="Planned ship date"
          value={plannedShipDate}
          onChange={onPlannedShipDateChange}
          slotProps={{
            textField: { size: "small", sx: { width: 190 } },
            field: { clearable: true },
          }}
          sx={{ flexShrink: 0 }}
        />
        <Button
          variant="contained"
          disabled={containerCount === 0 || creating}
          onClick={onCreate}
          // The label must not wrap, so the button must not be allowed to
          // shrink under it either — between the sm breakpoint and about
          // 700px the summary text would otherwise squeeze it and the
          // label would spill outside its own background.
          sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
        >
          Create shipment request
        </Button>
      </Stack>
    </Paper>
  );
}
